import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2 } from "lucide-react";
import { AGENDA_TIPI } from "@/lib/constants";

const empty = { tipo: "Appuntamento", titolo: "", data: "", ora: "", stato: "da_fare", portfolio_id: "", resoconto: "", esito: "", prossimo_passo: "", prossimo_data: "" };

export function AgendaDialog({ open, onOpenChange, initial, onSave, onDelete, portfolio = [], fixedPortfolio, defaultDate }) {
  const [form, setForm] = useState(empty);
  useEffect(() => {
    if (open) setForm(initial ? { ...empty, ...initial } : { ...empty, data: defaultDate || new Date().toISOString().slice(0, 10), portfolio_id: fixedPortfolio?.id || "" });
  }, [open, initial, defaultDate, fixedPortfolio]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const hasFollowup = !!initial?.followup_id;

  const submit = () => {
    if (!form.titolo.trim() || !form.data) return;
    const { id, followup_id, portfolio_nome, created_at, settore, ...data } = form; // eslint-disable-line no-unused-vars
    onSave({ ...data, titolo: data.titolo.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="agenda-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? "Modifica voce agenda" : "Nuova voce agenda"}</DialogTitle>
          <DialogDescription>Agenda personale del titolare: visibile solo a te.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => set("tipo", v)}>
                <SelectTrigger data-testid="agenda-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>{AGENDA_TIPI.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Società (Portfolio)</Label>
              <Select value={form.portfolio_id || "none"} disabled={!!fixedPortfolio} onValueChange={(v) => set("portfolio_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="agenda-portfolio-select"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna (personale)</SelectItem>
                  {(fixedPortfolio ? [fixedPortfolio] : portfolio).map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5"><Label>Titolo *</Label><Input data-testid="agenda-titolo-input" value={form.titolo} onChange={(e) => set("titolo", e.target.value)} placeholder="es. Colloquio con il presidente" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="grid gap-1.5"><Label>Data *</Label><Input data-testid="agenda-data-input" type="date" value={form.data} onChange={(e) => set("data", e.target.value)} /></div>
            <div className="grid gap-1.5"><Label>Ora</Label><Input data-testid="agenda-ora-input" type="time" value={form.ora || ""} onChange={(e) => set("ora", e.target.value)} /></div>
            <div className="grid gap-1.5">
              <Label>Stato</Label>
              <Select value={form.stato} onValueChange={(v) => set("stato", v)}>
                <SelectTrigger data-testid="agenda-stato-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="da_fare">Da fare</SelectItem><SelectItem value="fatto">Fatto</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5"><Label>Resoconto</Label><Textarea data-testid="agenda-resoconto-input" rows={3} value={form.resoconto} onChange={(e) => set("resoconto", e.target.value)} placeholder="Cosa vi siete detti, richieste, accordi…" /></div>
          <div className="grid gap-1.5"><Label>Esito</Label><Input data-testid="agenda-esito-input" value={form.esito} onChange={(e) => set("esito", e.target.value)} placeholder="es. Interessati, attendono preventivo" /></div>
          <div className="rounded-xl border border-dashed border-primary/30 p-3 grid gap-3">
            <div className="text-xs font-semibold text-primary">Prossimo passo {hasFollowup && <span className="text-emerald-600 font-normal">· promemoria già creato in agenda</span>}</div>
            <div className="grid grid-cols-3 gap-3">
              <div className="grid gap-1.5 col-span-2"><Input data-testid="agenda-prossimo-input" disabled={hasFollowup} value={form.prossimo_passo} onChange={(e) => set("prossimo_passo", e.target.value)} placeholder="es. Inviare preventivo" /></div>
              <div className="grid gap-1.5"><Input data-testid="agenda-prossimo-data" disabled={hasFollowup} type="date" value={form.prossimo_data || ""} onChange={(e) => set("prossimo_data", e.target.value)} /></div>
            </div>
            {!hasFollowup && <p className="text-[11px] text-slate-400">Con testo e data, al salvataggio viene creato in automatico un promemoria in agenda.</p>}
          </div>
        </div>
        <DialogFooter className="gap-2">
          {initial?.id && onDelete && (
            <Button data-testid="agenda-delete-button" variant="ghost" className="sm:mr-auto text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5" onClick={() => onDelete(initial)}><Trash2 className="h-4 w-4" /> Elimina</Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="agenda-submit-button" onClick={submit}>Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
